import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { ValidatorRuleType } from "@tKgpco/Common/Utilities/validate";
import { useForm } from "@tKgpco/Custom/Hooks/useForm";
import addNewTerm from "@tKgpco/Handlers/Terms/AddTerm";
import getWebTermCode from "@tKgpco/Handlers/Terms/GetTermCode";
import { AddTermApiParameters } from "@tKgpco/Services/TermsService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        code: state.KGPState.customTerms.quoteCode,
    };
};
const mapDispatchToProps = {
    addNewTerm,
    getWebTermCode
};

interface CustomWidgetStyles {
    mainGridContainer?: GridContainerProps;
    heading?: TypographyPresentationProps;
}

const styles: CustomWidgetStyles = {
    mainGridContainer: {
        offsetCss: css`
            max-width: 700px;
            margin: 0 auto;
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                );
            }}
        `,
    },
    heading: {
        css: css`
            text-transform: uppercase;
            font-size: 22px;
            color: #0b70b7;
            border-bottom: 1px solid #0b70b7;
            padding-bottom: 5px;
            width: 100%;
        `,
    },
};

const EMPTY_STRING = "";

interface TermsFormValues {
    termsCode: string;
    vendor: string;
    type: string;
    estimate: boolean;
    description: string;
    default: boolean;
}

const formValues: TermsFormValues = {
    termsCode: EMPTY_STRING,
    vendor: EMPTY_STRING,
    type: EMPTY_STRING,
    estimate: true,
    description: EMPTY_STRING,
    default: false
};

const initialErrorState: Omit<TermsFormValues, "estimate" | "default"> = {
    termsCode: EMPTY_STRING,
    vendor: EMPTY_STRING,
    type: EMPTY_STRING,
    description: EMPTY_STRING,
};

const validatorConfig: { [key in keyof TermsFormValues]: ValidatorRuleType } = {
    termsCode: {
        required: true,
        label: "TermsCode",
        requiredMsg: "Terms Code is required",
    },
    vendor: {
        required: true,
        label: "Vendor",
        requiredMsg: "Vendor is required",
    },
    type: {
        required: true,
        label: "Type",
        requiredMsg: "Type is required",
    },
    estimate: {
        required: false,
        label: "Estimate",
    },
    description: {
        required: true,
        label: "Description",
        requiredMsg: "Description is required",
    },
    default: {
        required: false,
        label: "Default",
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const AddTerms: React.FC<Props> = ({ id, code, addNewTerm, getWebTermCode, history }) => {
    const {
        fieldChangeHandler,
        validateField,
        updateFormState,
        updateFormErrors,
        formState,
        formErrors,
        isSubmitDisabled,
    } = useForm({
        formValues: {
            ...formValues,
            termsCode: code
        },
        initialErrorState,
        validatorConfig,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const createTerm = () => {
        setIsSubmitting(true);
        const params: AddTermApiParameters = {
            TermsCode: formState.termsCode,
            Vendor:formState.vendor,
            Type:formState.type,
            Estimate:formState.estimate,
            Description:formState.description,
            Default:formState.default

        };
        addNewTerm({
            ...params,
            onSuccess: () => {
                setIsSubmitting(false);
                updateFormState(formValues);
                updateFormErrors(initialErrorState);
                history.push("/MyAccount/Terms");
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Term added successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };

    useEffect(() => {
        getWebTermCode({});
    },[]);

    useEffect(() => {
        fieldChangeHandler({ name: "termsCode", value: code })
    },[code]);

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem width={12}>
                <Typography as="h2" {...styles.heading}>
                    {translate("Add New Term")}
                </Typography>
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Code")}
                    name="termsCode"
                    value={formState.termsCode}
                    // onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    // onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.termsCode && formErrors.termsCode}
                    iconProps={{
                        color: "danger",
                        src: formErrors.termsCode ? AlertTriangle : undefined,
                    }}
                    required
                    disabled
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Vendor Item Id")}
                    name="vendor"
                    value={formState.vendor}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.vendor && formErrors.vendor}
                    iconProps={{
                        color: "danger",
                        src: formErrors.vendor ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Type")}
                    name="type"
                    value={formState.type}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.type && formErrors.type}
                    iconProps={{
                        color: "danger",
                        src: formErrors.type ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <TextField
                    label={translate("Description")}
                    name="description"
                    value={formState.description}
                    onChange={e => fieldChangeHandler({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    onBlur={e => validateField({ name: e.currentTarget.name, value: e.currentTarget.value })}
                    error={!!formErrors.description && formErrors.description}
                    iconProps={{
                        color: "danger",
                        src: formErrors.description ? AlertTriangle : undefined,
                    }}
                    required
                />
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.estimate}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            estimate: !formState.estimate,
                        }))
                    }
                >
                    {translate("Estimate")}
                </Checkbox>
            </GridItem>
            <GridItem width={12}>
                <Checkbox
                    labelPosition="right"
                    checked={formState.default}
                    onChange={(_, value: boolean) =>
                        updateFormState(() => ({
                            ...formState,
                            default: !formState.default,
                        }))
                    }
                >
                    {translate("Default")}
                </Checkbox>
            </GridItem>
            <GridItem>
                <Button onClick={createTerm} disabled={isSubmitDisabled || isSubmitting}>
                    Submit
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const addTerms: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(AddTerms)),
    definition: {
        group: "Basic",
    },
};

export default addTerms;
